import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { formatDuration, formatElapsedSince } from '../../../../utils/format';

// Define all possible report generation steps
const REPORT_STEPS = [
  { id: 'loading', label: 'Loading transcript', progress: 5 },
  { id: 'preparing', label: 'Preparing report generation', progress: 10 },
  { id: 'summary', label: 'Generating brief summary', progress: 15 },
  { id: 'keypoints', label: 'Extracting major keypoints', progress: 20 },
  { id: 'speakers', label: 'Extracting speakers', progress: 25 },
  { id: 'arguments', label: 'Extracting main arguments', progress: 30 },
  { id: 'connecting', label: 'Connecting speakers with arguments', progress: 40 },
  { id: 'additional', label: 'Extracting additional arguments', progress: 50 },
  { id: 'agreements', label: 'Extracting agreements and disagreements', progress: 60 },
  { id: 'takeaways', label: 'Extracting takeaways', progress: 70 },
  { id: 'insights', label: 'Extracting insightful comments', progress: 75 },
  { id: 'questions', label: 'Extracting follow-up questions', progress: 80 },
  { id: 'full_report', label: 'Generating full session report', progress: 85 },
  { id: 'review', label: 'Reviewing and rewriting report', progress: 90 },
  { id: 'building', label: 'Building final report structure', progress: 95 },
  { id: 'saving', label: 'Saving report', progress: 100 }
];

/**
 * ReportStatusDisplay Component
 * 
 * Displays the current status of report generation with elapsed time and queue information.
 * 
 * @param {Object} props
 * @param {string} props.status - Status: PENDING, PROCESSING, COMPLETED, FAILED
 * @param {string} props.startedAt - ISO timestamp when processing started
 * @param {string} props.completedAt - ISO timestamp when processing completed
 * @param {string} props.queuedAt - ISO timestamp when task was queued
 * @param {number} props.duration - Duration in seconds (for completed reports)
 * @param {string} props.errorMessage - Error message if failed
 * @param {number} props.queuePosition - Position in queue (if in queue)
 * @param {number} props.estimatedWaitTime - Estimated wait time in seconds (if in queue)
 * @param {string} props.currentStep - Current processing step name
 * @param {number} props.progress - Progress percentage (0-100)
 * @param {boolean} props.compact - Compact display mode
 */
const ReportStatusDisplay = ({
  status,
  startedAt,
  completedAt,
  queuedAt,
  duration,
  errorMessage,
  queuePosition,
  estimatedWaitTime,
  currentStep,
  progress,
  compact = false
}) => {
  const [elapsedTime, setElapsedTime] = useState(null);

  // Calculate elapsed time from queuedAt (or startedAt if queuedAt not available)
  useEffect(() => {
    if (status !== 'PROCESSING' && status !== 'PENDING') {
      setElapsedTime(null);
      return;
    }

    const updateElapsed = () => {
      // Counted from queueing when known, otherwise from the start.
      const startTime = queuedAt || startedAt;
      setElapsedTime(startTime ? formatElapsedSince(startTime) : null);
    };

    // Update immediately
    updateElapsed();

    // Update every second
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [status, startedAt, queuedAt]);

  // Get current step index based on currentStep or progress
  const getCurrentStepIndex = () => {
    if (!currentStep && progress !== null && progress !== undefined) {
      // Find step based on progress
      for (let i = REPORT_STEPS.length - 1; i >= 0; i--) {
        if (progress >= REPORT_STEPS[i].progress) {
          return i;
        }
      }
      return 0;
    }
    
    // Find step by matching currentStep label
    if (currentStep) {
      const stepIndex = REPORT_STEPS.findIndex(step => 
        currentStep.toLowerCase().includes(step.id) || 
        currentStep.toLowerCase().includes(step.label.toLowerCase())
      );
      if (stepIndex >= 0) return stepIndex;
    }
    
    return -1;
  };

  const currentStepIndex = getCurrentStepIndex();
  const displayProgress = progress !== null && progress !== undefined ? progress : 0;

  // Render based on status
  const renderStatus = () => {
    switch (status) {
      case 'PENDING':
        return (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
              <ClockIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
              <span className="text-gray-600">Waiting to start...</span>
              {elapsedTime && (
                <span className="text-xs text-gray-500 ml-2">
                  (Elapsed: {elapsedTime})
                </span>
              )}
            </div>
            {queuePosition !== null && queuePosition !== undefined && (
              <div className="text-xs text-gray-500">
                Queue position: #{queuePosition}
                {estimatedWaitTime && ` • Est. wait: ${formatDuration(estimatedWaitTime)}`}
              </div>
            )}
          </div>
        );

      case 'PROCESSING':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
                <ArrowPathIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-blue-500 animate-spin`} />
                <span className="text-blue-700 font-medium">
                  {currentStep || 'Processing...'}
                </span>
                {elapsedTime && (
                  <span className="text-xs text-gray-500 ml-2">
                    ({elapsedTime})
                  </span>
                )}
              </div>
              {displayProgress > 0 && (
                <span className="text-sm font-semibold text-blue-600">
                  {displayProgress}%
                </span>
              )}
            </div>
            
            {/* Progress bar */}
            {!compact && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${displayProgress}%` }}
                />
              </div>
            )}
            
            {/* Step indicator */}
            {!compact && currentStepIndex >= 0 && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-700 mb-2">Progress:</div>
                <div className="space-y-1.5">
                  {REPORT_STEPS.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    
                    return (
                      <div 
                        key={step.id} 
                        className={`flex items-center gap-2 text-xs ${
                          isCompleted ? 'text-green-600' : 
                          isCurrent ? 'text-blue-600 font-medium' : 
                          'text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-4 w-4 text-green-500" />
                        ) : isCurrent ? (
                          <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin" />
                        ) : (
                          <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                        )}
                        <span>{step.label}</span>
                        {isCurrent && displayProgress > 0 && (
                          <span className="text-gray-500 ml-auto">({displayProgress}%)</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 'COMPLETED':
        return (
          <div className="space-y-2">
            <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
              <CheckCircleIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-green-500`} />
              <span className="text-green-700 font-medium">
                Completed{duration ? ` in ${formatDuration(duration)}` : ''}
              </span>
            </div>
            {!compact && displayProgress === 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '100%' }} />
              </div>
            )}
          </div>
        );

      case 'FAILED':
        return (
          <div className="space-y-2">
            <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
              <ExclamationCircleIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-red-500`} />
              <span className="text-red-700">
                Failed{elapsedTime ? ` after ${elapsedTime}` : ''}
              </span>
            </div>
            {errorMessage && !compact && (
              <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
                <strong>Error:</strong> {errorMessage.substring(0, 200)}
                {errorMessage.length > 200 && '...'}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
            <span className="text-gray-500">No status available</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-2">
      {renderStatus()}
    </div>
  );
};

export default ReportStatusDisplay;

